import { useCallback, useEffect, useRef, useState } from 'react';
import { IconRefresh, IconX, IconLoader2 } from '@tabler/icons-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

interface Person {
  personId: number;
  uuid: string;
  firstName: string;
  lastName: string;
  idNumber: string | null;
  passportNumber: string | null;
  passportCountry: string | null;
  dateOfBirth: string | null;
  email: string | null;
  phoneNumber: string | null;
  ficaStatus: string;
  lastVerificationDate: string | null;
  createdAt: string;
  residentialAddress?: {
    addressId: number;
    line1: string | null;
    line2: string | null;
    suburb: string | null;
    city: string | null;
    province: string | null;
    postalCode: string | null;
    country: string | null;
  } | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export function PeoplePage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const searchDebounceRef = useRef<number | null>(null);

  const fetchPeople = useCallback(
    async (override?: { search?: string; page?: number; limit?: number }) => {
      const searchTerm = override?.search ?? search;
      const currentPage = override?.page ?? page;
      const limit = override?.limit ?? rowsPerPage;

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, any> = {
          page: currentPage + 1,
          limit,
        };

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        const response = await api.get('/natural-persons', { params });
        const { data, pagination: pager } = response.data;

        setPeople(data || []);
        setPagination(pager || null);
        if (pager) {
          setPage(Math.max(0, (pager.page ?? 1) - 1));
          setRowsPerPage(pager.limit ?? limit);
        }
      } catch (err: any) {
        console.error('Failed to load people', err);
        setError(err.response?.data?.message || 'Failed to load people');
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, search],
  );

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople]);

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    setPage(0);
    setSearch(nextSearch);
    fetchPeople({ search: nextSearch, page: 0 });
  };

  const handleRefresh = () => {
    setSearchInput(search);
    fetchPeople();
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status.toLowerCase()) {
      case 'verified':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'rejected':
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handlePersonClick = async (person: Person) => {
    setSelectedPerson(person);
    setDetailError(null);
    setDetailLoading(true);

    try {
      const response = await api.get(`/natural-persons/${person.personId}`);
      if (response.data?.data) {
        setSelectedPerson(response.data.data);
      }
    } catch (err: any) {
      console.log('Could not fetch detailed person data, using available data');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedPerson(null);
    setDetailError(null);
  };

  const totalPages = pagination ? pagination.pages : 0;

  return (
    <div>
      <PageHeader
        title="People"
        subtitle="Searchable list of all people and their verification status."
        action={
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <IconRefresh size={16} />
            Refresh
          </Button>
        }
      />

      <MainCard className="mb-6">
        <div className="flex flex-col md:flex-row gap-3 items-center">
          <Input
            placeholder="Search by name, email, ID number, or passport"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              if (searchDebounceRef.current) {
                window.clearTimeout(searchDebounceRef.current);
              }
              searchDebounceRef.current = window.setTimeout(() => {
                if (e.target.value.trim().length >= 2 || e.target.value.trim().length === 0) {
                  setSearch(e.target.value.trim());
                  setPage(0);
                  fetchPeople({ search: e.target.value.trim(), page: 0 });
                }
              }, 500);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSearch();
              }
            }}
            className="flex-1"
          />
        </div>
      </MainCard>

      <MainCard>
        {loading && (
          <div className="flex items-center justify-center py-16">
            <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && error && (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {!loading && !error && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ID Number</TableHead>
                  <TableHead>Passport</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Date of Birth</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Verified</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {people.map((person) => (
                  <TableRow
                    key={person.personId}
                    className="cursor-pointer"
                    onClick={() => handlePersonClick(person)}
                  >
                    <TableCell>
                      <span className="font-semibold">
                        {person.firstName} {person.lastName}
                      </span>
                    </TableCell>
                    <TableCell>{person.idNumber || '-'}</TableCell>
                    <TableCell>
                      {person.passportNumber
                        ? `${person.passportNumber}${person.passportCountry ? ` (${person.passportCountry})` : ''}`
                        : '-'}
                    </TableCell>
                    <TableCell>{person.email || '-'}</TableCell>
                    <TableCell>{person.phoneNumber || '-'}</TableCell>
                    <TableCell>{formatDate(person.dateOfBirth)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(person.ficaStatus)}>
                        {person.ficaStatus}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(person.lastVerificationDate)}</TableCell>
                  </TableRow>
                ))}
                {people.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <div className="py-12 text-center">
                        <p className="text-muted-foreground">
                          {search.trim()
                            ? 'No people found matching your search.'
                            : 'No people found yet.'}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {pagination && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page:</span>
                  <Select
                    value={String(rowsPerPage)}
                    onValueChange={(val) => {
                      if (val) {
                        const newLimit = parseInt(val, 10);
                        setRowsPerPage(newLimit);
                        setPage(0);
                        fetchPeople({ limit: newLimit, page: 0 });
                      }
                    }}
                  >
                    <SelectTrigger className="w-[70px]" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map((opt) => (
                        <SelectItem key={opt} value={String(opt)}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>
                    {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, pagination.total)} of {pagination.total}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(totalPages - 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </MainCard>

      <Dialog
        open={Boolean(selectedPerson)}
        onOpenChange={(open) => { if (!open) handleCloseModal(); }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPerson ? `${selectedPerson.firstName} ${selectedPerson.lastName}` : 'Person Details'}
            </DialogTitle>
          </DialogHeader>

          {detailLoading && (
            <div className="flex justify-center py-8">
              <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {!detailLoading && detailError && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{detailError}</AlertDescription>
            </Alert>
          )}

          {!detailLoading && selectedPerson && (
            <div className="space-y-5">
              <Alert>
                <AlertDescription>
                  Person ID: <strong>{selectedPerson.personId}</strong> &bull; UUID: <strong>{selectedPerson.uuid}</strong>
                  <br />
                  Created: {formatDateTime(selectedPerson.createdAt)}
                </AlertDescription>
              </Alert>

              <Separator />

              <div>
                <h3 className="text-base font-semibold mb-3">Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">First Name</Label>
                    <p className="text-sm">{selectedPerson.firstName}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Last Name</Label>
                    <p className="text-sm">{selectedPerson.lastName}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date of Birth</Label>
                    <p className="text-sm">{formatDate(selectedPerson.dateOfBirth)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">FICA Status</Label>
                    <div className="mt-0.5">
                      <Badge variant={getStatusVariant(selectedPerson.ficaStatus)}>
                        {selectedPerson.ficaStatus}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-base font-semibold mb-3">Identity Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">ID Number</Label>
                    <p className="text-sm">{selectedPerson.idNumber || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Passport</Label>
                    <p className="text-sm">
                      {selectedPerson.passportNumber
                        ? `${selectedPerson.passportNumber}${selectedPerson.passportCountry ? ` (${selectedPerson.passportCountry})` : ''}`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-base font-semibold mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="text-sm">{selectedPerson.email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone Number</Label>
                    <p className="text-sm">{selectedPerson.phoneNumber || '-'}</p>
                  </div>
                </div>
              </div>

              {selectedPerson.residentialAddress && (
                <>
                  <Separator />
                  <div>
                    <h3 className="text-base font-semibold mb-3">Residential Address</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">Address Line 1</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.line1 || '-'}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">Address Line 2</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.line2 || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Suburb</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.suburb || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">City</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.city || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Province</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.province || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Postal Code</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.postalCode || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Country</Label>
                        <p className="text-sm">{selectedPerson.residentialAddress.country || '-'}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              <div>
                <h3 className="text-base font-semibold mb-3">Verification Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Last Verification Date</Label>
                    <p className="text-sm">{formatDate(selectedPerson.lastVerificationDate)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
